import axios, { AxiosError } from "axios";
import i18n from "@/i18n";
// import { clearAuth } from "./auth";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v2";

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sij_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["Accept-Language"] = i18n.language || "ar";
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    // if (error.response?.status === 401 || error.response?.status === 403) {
    //   clearAuth();
    //   if (!window.location.pathname.startsWith("/login")) {
    //     window.location.assign("/login");
    //   }
    // }
    return Promise.reject(error);
  },
);

export type Paginated<T> = {
  data: T[];
  meta: { total: number; per_page: number; current_page: number; last_page: number };
};

export type ListParams = {
  search?: string;
  contract_title?: string;
  status?: string | number;
  package_id?: number;
  specialization_id?: number;
  major_id?: number;
  doctor_id?: number;
  reviewer_id?: number;
  reviewer?: string;
  file_number?: string;
  central_id?: number;
  tier?: string;
  date_from?: string;
  date_to?: string;
  created_at?: string;
  from?: string;
  to?: string;
  user_id?: number;
  moderator_id?: number;
  action?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  /** Unified-list sort string (§A1), e.g. "-created_at,name". */
  sort?: string;
  page?: number;
  per_page?: number;
  name?: string;
  mobile?: string;
  email?: string;
  license_number?: string;
  subscription_status?: string;
  title?: string;
  doctor_name?: string;
  reviewer_name?: string;
  reviewer_mobile?: string;
  reviewer_file_number?: string;
  reviewer_id_number?: string;
  id_number?: string;
};

type ValidationPayload = {
  message?: string;
  // Normally { field: ["msg"] }, but some endpoints return a raw string
  // (e.g. an unhandled DB exception message).
  errors?: Record<string, string[]> | string;
  details?: Record<string, unknown>;
  status?: {
    message?: string;
    error_code?: string;
    error_list?: Array<{ field_name: string; message: string }>;
  };
  data?: {
    error_list?: Array<{ field_name: string; message: string }>;
    message?: string;
  };
};

function normalizeFieldKey(key: string): string {
  return key.trim().toLowerCase();
}

const FIELD_KEY_ALIASES: Record<string, string> = {
  id_number: "national_id",
  idnumber: "national_id",
  national_id: "national_id",
  file_number: "file_number",
  filenumber: "file_number",
};

function mapBackendFieldToForm(fieldName: string): string {
  const normalized = normalizeFieldKey(fieldName);
  return FIELD_KEY_ALIASES[normalized] ?? fieldName;
}

/** Guess which form field a raw DB duplicate-entry message refers to. */
export function detectDuplicateField(text: string): string | null {
  const lower = text.toLowerCase();
  if (lower.includes("mobile")) return "mobile";
  if (lower.includes("e-mail") || lower.includes("email")) return "email";
  if (lower.includes("medical_pro_class") || lower.includes("license")) return "medical_pro_class_number";
  if (lower.includes("national") || lower.includes("id_number") || lower.includes("idnumber")) return "national_id";
  if (lower.includes("file_number") || lower.includes("filenumber")) return "file_number";
  return null;
}

const SQLSTATE_RE = /^\s*SQLSTATE|duplicate entry|1062|23000/i;

export function parseValidationError(error: unknown): Record<string, string> {
  if (!axios.isAxiosError(error)) return {};
  const body = error.response?.data as ValidationPayload & Record<string, unknown> | undefined;
  if (!body) return {};

  // Standard Laravel format: { errors: { field: ["msg"] } }
  if (body.errors && typeof body.errors === "object") {
    const out: Record<string, string> = {};
    for (const [key, messages] of Object.entries(body.errors)) {
      const msgs = messages as string[];
      if (msgs[0]) out[mapBackendFieldToForm(key)] = msgs[0];
    }
    if (Object.keys(out).length) return out;
  }

  // Legacy shape: { message, errors: "<raw string>" } — e.g. raw SQL text from
  // an unhandled DB exception. Attach duplicates to the right field so the
  // message shows inline instead of vanishing into a generic toast.
  if (typeof body.errors === "string" && body.errors.trim()) {
    const raw = body.errors.trim();
    const field = detectDuplicateField(raw);
    if (field && SQLSTATE_RE.test(raw)) {
      return { [mapBackendFieldToForm(field)]: "This value is already registered. Please use a different one." };
    }
    // Non-field string error — surfaced via extractServerMessages(), never raw SQL.
    if (!SQLSTATE_RE.test(raw)) return { _server: raw };
  }

  // Sijilaty Api format: status.error_list or data.error_list = [{ field_name, message }]
  const errorList =
    (body.status as { error_list?: Array<{ field_name: string; message: string }> } | undefined)?.error_list ??
    (body.data as { error_list?: Array<{ field_name: string; message: string }> } | undefined)?.error_list ??
    (body as { error_list?: Array<{ field_name: string; message: string }> } | undefined)?.error_list;

  if (Array.isArray(errorList) && errorList.length) {
    const out: Record<string, string> = {};
    for (const item of errorList) {
      if (item?.field_name && item?.message) {
        const formKey = mapBackendFieldToForm(item.field_name);
        // first message per field wins
        if (!out[formKey]) out[formKey] = item.message;
      }
    }
    if (Object.keys(out).length) return out;
  }

  return {};
}

/**
 * Collect every human-readable server message from an error response:
 * error_list entries, errors-object entries, top-level message and details.
 * Raw SQL text is never included.
 */
export function extractServerMessages(error: unknown): string[] {
  if (!axios.isAxiosError(error)) return [];
  const body = error.response?.data as ValidationPayload & Record<string, unknown> | undefined;
  if (!body || typeof body !== "object") return [];
  const out: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === "string") {
      const s = v.trim();
      if (s && !SQLSTATE_RE.test(s) && !out.includes(s)) out.push(s);
    }
  };

  const errorList =
    (body.status as { error_list?: Array<{ message: string }> } | undefined)?.error_list ??
    (body.data as { error_list?: Array<{ message: string }> } | undefined)?.error_list ??
    (body as { error_list?: Array<{ message: string }> } | undefined)?.error_list;
  if (Array.isArray(errorList)) for (const item of errorList) push(item?.message);

  if (body.errors && typeof body.errors === "object") {
    for (const messages of Object.values(body.errors)) {
      const msgs = messages as string[];
      if (Array.isArray(msgs)) for (const m of msgs) push(m);
    }
  }

  push((body.status as { message?: string } | undefined)?.message);
  push((body.data as { message?: string } | undefined)?.message);
  push(body.message);

  const details = body.details;
  if (details && typeof details === "object") {
    for (const v of Object.values(details)) {
      if (Array.isArray(v)) for (const m of v) push(m);
      else push(v);
    }
  }

  return out;
}

export function apiErrorMessage(error: unknown, fallback = "Request failed"): string {
  if (!axios.isAxiosError(error)) return fallback;
  const body = error.response?.data as ValidationPayload & Record<string, unknown> | undefined;
  if (!body) return fallback;

  // Legacy string-errors shape: translate SQL duplicates instead of showing them raw.
  if (typeof body.errors === "string" && body.errors.trim()) {
    const field = detectDuplicateField(body.errors);
    if (field && SQLSTATE_RE.test(body.errors)) {
      return "This value is already registered. Please use a different one.";
    }
  }

  // Prefer first field error message for validation failures (more specific than generic status.message)
  const errorList =
    (body.status as { error_list?: Array<{ message: string }> } | undefined)?.error_list ??
    (body.data as { error_list?: Array<{ message: string }> } | undefined)?.error_list ??
    (body as { error_list?: Array<{ message: string }> } | undefined)?.error_list;

  if (Array.isArray(errorList) && errorList.length && errorList[0]?.message) {
    // If status.message is generic validation_error, prefer field message.
    // Check the HTTP status too: the backend localizes the generic message
    // (e.g. Arabic "خطأ في التحقق"), so text matching alone misses it.
    const statusMsg = (body.status as { message?: string } | undefined)?.message ?? body.message;
    const isGenericValidation =
      error.response?.status === 422 ||
      !statusMsg ||
      /validation/i.test(statusMsg) ||
      statusMsg === "validation_error";
    if (isGenericValidation) {
      return errorList[0].message;
    }
  }

  if (body.status?.message && !/validation_error/i.test(body.status.message)) return body.status.message;
  if ((body.data as { message?: string } | undefined)?.message) return (body.data as { message?: string }).message!;
  if (body.message) return body.message;
  return fallback;
}

export function apiErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const body = error.response?.data as ValidationPayload | undefined;
  return body?.details;
}
